import { Box, Typography, Paper } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';

import Input from 'components/Common/Input';
import TelInput from 'components/Common/TelInput';
import Button from 'components/Common/Button';
import GoBackButtonIcon from '@/assets/icons/GoBackButtonIcon';

import { useCreateOrUpdateOperator, useGetOperator } from 'apis/business/setting/operator/hook';
import OperatorFormSkeleton from './OperatorFormSkeleton';
import { operatorSchema } from 'utils/schema/validator/OperatorValidation';
import WorkingHours from './workingHours';

export default function OperatorForm({ onClose, title }: { onClose?: () => void; title?: string }) {
  const navigate = useNavigate();
  const { operatorId } = useParams<{ operatorId: string }>();

  const { data: operator, isLoading } = useGetOperator(operatorId!);
  const { mutate: handleOperator, isPending } = useCreateOrUpdateOperator(Boolean(!onClose));

  const { handleSubmit, control, watch, getValues, setValue } = useForm<any>({
    resolver: zodResolver(operatorSchema),
    defaultValues: { operator: { working_hours: defaultWorkingHours, holidays: [], sms_consent: false } },
    values: operatorId ? { operator } : {}
  });

  const email = watch('operator.email');
  const phone = watch('operator.phone_number');

  const hasEmail = Boolean((email ?? '').trim());
  const hasPhone = Boolean((phone ?? '').replace(/[^\d]/g, '').length > 4);

  const onSubmit = async (data: any) => {
    await handleOperator(data);
    onClose && onClose();
  };

  if (isLoading) return <OperatorFormSkeleton />;

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)}>
      {/* Header */}
      <Box display='flex' alignItems='center' mb={3} gap={0.8}>
        <Box
          onClick={onClose ? () => onClose() : () => navigate(-1)}
          display='flex'
          alignItems='center'
          justifyContent='center'
          sx={{ cursor: 'pointer' }}
        >
          <GoBackButtonIcon />
        </Box>
        <Typography variant='h6' fontWeight={500}>
          {title ? title : operatorId ? 'Edit Operator' : 'Add Operator'}
        </Typography>
      </Box>

      {/* Main Card */}
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          p: 3,
          border: '1px solid #ECECEC'
        }}
      >
        {/* Basic Info */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Input
              name='operator.first_name'
              label='Name'
              control={control}
              placeholder='Enter Name'
              fullWidth
              required
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Input
              name='operator.email'
              label='Email'
              control={control}
              placeholder='Contact@business.com'
              fullWidth
              required={!hasPhone}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TelInput
              name='operator.phone_number'
              label='Phone Number'
              control={control}
              defaultCountry='US'
              fullWidth
              consent_key='operator.sms_consent'
              required={!hasEmail}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Input
              name='operator.sip_uri'
              label='SIP URI'
              control={control}
              placeholder='sip:user@domain.com'
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Input
              name='operator.extension'
              label='Extension Number'
              control={control}
              placeholder='Enter Extension Number'
              type='number'
              fullWidth
            />
          </Grid>
        </Grid>

        {/* Schedule */}
        <Box mt={4}>
          <WorkingHours getValues={getValues} setValue={setValue} control={control} />
        </Box>
      </Paper>

      {/* Footer Actions */}
      <Box display='flex' justifyContent='end' gap={1} mt={3}>
        <Button
          color='secondary'
          variant='outlined'
          sx={{ border: '1px solid #F3F3F3', background: '#fff' }}
          onClick={onClose ? () => onClose() : () => navigate(-1)}
        >
          Cancel
        </Button>
        <Button type='submit' variant='contained' isPending={isPending}>
          {operatorId ? 'Save Operator' : title ? title : 'Add Operator'}
        </Button>
      </Box>
    </Box>
  );
}

const defaultWorkingHours = {
  monday: { open: false, from_time: '', to_time: '', breaks: [] },
  tuesday: { open: false, from_time: '', to_time: '', breaks: [] },
  wednesday: { open: false, from_time: '', to_time: '', breaks: [] },
  thursday: { open: false, from_time: '', to_time: '', breaks: [] },
  friday: { open: false, from_time: '', to_time: '', breaks: [] },
  saturday: { open: false, from_time: '', to_time: '', breaks: [] },
  sunday: { open: false, from_time: '', to_time: '', breaks: [] }
};
